import type {
  Affix,
  AffixId,
  BasicDie,
  DerivedStats,
  DieResult,
  DieSides,
  DieSpellId,
  LoadedFaces,
  MasterDie,
  Rarity,
} from './types';
import { DIE_SPELL_COUNT, dieSpellsOfPool } from '../data/dieSpells';
import { AFFIX_BY_ID, NAMED_PRISMATICS, affixAppliesTo } from '../data/affixes';
import { BALANCE } from '../data/balance';
import { affixValue, dominantAffix, hasAffix, rollAffixes } from './affixes';
import type { Rng } from './rng';

/**
 * Facteur de dégâts du **niveau** d'un dé Maître (v0.46.0, A94-5) :
 * `dieLevelFactor^niveau` (2 depuis A110, 1,5 avant), ×1 sur un dé sans niveau (dé de campagne). La contribution du
 * dé à `D_L` est `face × facteur` ; la face brute reste celle des sorts.
 */
export type DieFactorStats = Partial<Pick<DerivedStats, 'dieLevelFactor' | 'corruptedFlatFactor'>>;

export function dieLevelFactor(die: Pick<MasterDie, 'level'> | null | undefined, stats?: DieFactorStats | null): number {
  const level = die?.level;
  if (typeof level !== 'number' || !(level > 0)) return 1;
  // A111 : la surcharge de debug (via les stats dérivées) prime sur la constante.
  return Math.pow(stats?.dieLevelFactor ?? BALANCE.dieLevelFactor, level);
}

/**
 * Facteur des dés **Corrupted** (v0.49.0, COMMENTS v0.48.1 §1.6, A98-6) :
 * `BALANCE.corruptedFlatFactor` (Mult_Corrupted, ×5 000 depuis A114) sur un dé marqué `corrupted`, ×1 sur
 * un dé de campagne.
 */
export function corruptedFactor(die: Pick<MasterDie, 'corrupted'> | null | undefined, stats?: DieFactorStats | null): number {
  return die?.corrupted === true ? (stats?.corruptedFlatFactor ?? BALANCE.corruptedFlatFactor) : 1;
}

/**
 * Facteur complet de la face d'un dé Maître dans `D_L` : niveau × Corrupted,
 * `1,5^niveau × corruptedFlatFactor` sur un dé Corrupted, ×1 sur un dé de campagne.
 */
export function dieDamageFactor(die: Pick<MasterDie, 'level' | 'corrupted'> | null | undefined, stats?: DieFactorStats | null): number {
  return dieLevelFactor(die, stats) * corruptedFactor(die, stats);
}

/** Options de tirage d'une face. */
export interface FaceOptions {
  /** Probabilité (0..1) d'obtenir directement la face maximale (affixe Lesté). */
  weightedChance?: number;
  /** Deux tirages, le meilleur est conservé (affixe Avantageux). */
  advantage?: boolean;
  /**
   * Affixe *Pipé* (A20) : faces remplacées par la face cible. La substitution
   * a lieu **avant** *Lesté* et *Avantageux* — c'est-à-dire sur le tirage brut,
   * dont le maximum d'*Avantageux* est ensuite pris.
   */
  loaded?: LoadedFaces | null;
}

/** Applique *Pipé* à une face brute : une face remplacée devient la face cible. */
export function applyLoaded(face: number, loaded?: LoadedFaces | null): number {
  if (!loaded) return face;
  return loaded.replaced.includes(face) ? loaded.face : face;
}

/**
 * Faces de *Pipé* **effectives** d'un dé : `die.loaded`, sauf sur un dé où
 * l'affixe ne peut pas agir (A89-4 : un d100 qui porterait *Pipé* par une
 * sauvegarde modifiée se joue sans l'effet).
 */
export function effectiveLoaded(die: Pick<MasterDie, 'sides' | 'loaded'>): LoadedFaces | null {
  if (!die.loaded) return null;
  return affixAppliesTo('loaded', die.sides) ? die.loaded : null;
}

/** Un tirage simple, Pipé puis Lesté inclus. */
function singleFace(
  sides: number,
  rng: Rng,
  weightedChance: number,
  loaded?: LoadedFaces | null,
): number {
  if (weightedChance > 0 && rng.chance(weightedChance)) return sides;
  return applyLoaded(rng.die(sides), loaded);
}

/**
 * Tire la face d'un dé.
 * Pipé remplace la face brute par sa face cible ; Lesté remplace le tirage par
 * la face maximale ; Avantageux répète la procédure complète deux fois et garde
 * le meilleur résultat.
 */
export function rollFace(sides: number, rng: Rng, opts: FaceOptions = {}): number {
  const weighted = opts.weightedChance ?? 0;
  const a = singleFace(sides, rng, weighted, opts.loaded);
  if (!opts.advantage) return a;
  const b = singleFace(sides, rng, weighted, opts.loaded);
  return Math.max(a, b);
}

/** Contexte commun à tous les dés d'un même lancer. */
export interface ThrowContext {
  stats: DerivedStats;
  /** La cible est-elle sous le seuil d'exécution au moment du lancer ? */
  executing: boolean;
  /** Seuil d'exécution appliqué (0,2 par défaut). */
  executeThreshold: number;
}

/**
 * Tirage complet d'un dé Maître : face puis **contribution à `D_L`**.
 *
 * COMMENTS v0.3.0 §1.2.1 : `D_L = d_M + d_B + Σ d_F`. Un dé Maître apporte donc
 * sa face **plus** les dégâts plats de *Petit lancer* (« +1 dégât de base par dé
 * équipé »). Tous les multiplicateurs — *Main lestée* (`M_T`), sort (`D_S`),
 * critique (`D_C`), *Exécutant* — sont désormais appliqués **une seule fois au
 * lancer entier** dans `core/combat.ts`, plus dé par dé.
 *
 * v0.46.0 (A94-5) : la face compte `face × 1,5^niveau` (`dieLevelFactor`) ;
 * `face` reste la face **brute** (sorts, télémétrie, vue 3D). v0.49.0
 * (A98-6) : × 1000 sur un dé Corrupted (`dieDamageFactor`).
 */
export function rollMasterDie(die: MasterDie, ctx: ThrowContext, rng: Rng): DieResult {
  const face = rollFace(die.sides, rng, {
    // Affixe Lesté (+ surcharge de debug éventuelle ; plus aucun nœud n'en donne).
    weightedChance: affixValue(die, 'weighted') + ctx.stats.masterWeightedChance,
    advantage: affixValue(die, 'advantaged') > 0,
    loaded: effectiveLoaded(die),
  });
  return {
    dieId: die.id,
    isMaster: true,
    sides: die.sides,
    face,
    damage: face * dieDamageFactor(die, ctx.stats) + ctx.stats.flatDamagePerDie,
    crit: false,
    absorbed: false,
  };
}

/**
 * Tirage d'un dé **Basique** du contenant — spec §11.1.2.
 *
 * Ni affixe, ni combinaison, ni critique propre : ces dés ne fournissent que du
 * volume. Leur face compte **en entier** dans `d_B` depuis la v0.4.0 : le malus
 * 1/5 du Gobelet (spec §11.1.2) n'apparaît plus dans la formule §1.2 (Q27).
 * Ils ne reçoivent **ni** les dégâts plats de *Petit lancer* **ni** le bonus
 * d'*Exécutant* (DECISIONS Q20) : ces deux effets appartiennent aux dés Maîtres.
 */
export function rollBasicDie(die: BasicDie, rng: Rng): DieResult {
  const face = rollFace(die.sides, rng);
  return {
    dieId: null,
    isMaster: false,
    sides: die.sides,
    face,
    damage: face,
    crit: false,
    absorbed: false,
    rarity: die.rarity,
  };
}

// ─────────────────────────────────────────────────── Création des dés Maîtres

/** Matériaux évocateurs par rareté (préposition incluse pour éviter les accords). */
const MATERIALS: Record<Rarity, string[]> = {
  common: ["d'os", 'de bois', "d'argile"],
  uncommon: ['de bronze', 'de cuivre', 'de silex'],
  rare: ["d'acier", "d'argent", "d'obsidienne"],
  epic: ['de mithril', "d'améthyste", "d'ébène"],
  legendary: ["d'orichalque", "d'astre", 'de sang-dragon'],
  prismatic: ['de prisme', "d'aurore"],
};

/** Nom court et évocateur : « Dé <adjectif de l'affixe dominant> <matériau> ». */
export function generateDieName(die: Omit<MasterDie, 'name'>, rng: Rng): string {
  const pool = MATERIALS[die.rarity] ?? MATERIALS.common;
  const material = pool[rng.int(0, pool.length - 1)];
  const adjective = dominantAffix(die as MasterDie)?.adjective ?? 'brut';
  return `Dé ${adjective} ${material}`;
}

/** Identifiant de dé court et déterministe vis-à-vis du Rng. */
export function makeDieId(rng: Rng): string {
  const a = rng.int(0, 0xffffff).toString(36);
  const b = rng.int(0, 0xffffff).toString(36);
  return `d_${a}${b}`;
}

/**
 * Sorts d'un dé neuf, selon sa rareté — COMMENTS v0.4.1 §1.4 / arbitrage A3 :
 *
 * | Rareté | Sorts |
 * |---|---|
 * | Commun, Inhabituel | 1 sort **commun** |
 * | Rare | 1 sort, **30 %** d'être rare |
 * | Épique | 2 sorts distincts, **30 %** par sort d'être rare |
 * | Légendaire (et Prismatique, hors jeu) | 2 sorts distincts, **un rare garanti** |
 *
 * Les sorts sont toujours **distincts**. v0.49.0 (A98-4) : `sides` filtre les
 * pools par `minSides` — *Vingt-dicte*, *Null* et *Tempête* dès le d12,
 * *Rouleau Compresseur* sur le d20 ; les anciens sorts vont sur tous les dés
 * (sans `sides`, aucun filtre).
 */
export function rollDieSpells(rarity: Rarity, rng: Rng, sides?: number): DieSpellId[] {
  const count = DIE_SPELL_COUNT[rarity] ?? 1;
  const commons = dieSpellsOfPool('common', sides);
  const rares = dieSpellsOfPool('rare', sides);
  const canBeRare = rarity === 'rare' || rarity === 'epic' || rarity === 'legendary' || rarity === 'prismatic';
  const guaranteed = rarity === 'legendary' || rarity === 'prismatic';

  const out: DieSpellId[] = [];
  for (let i = 0; i < count; i++) {
    const rare = canBeRare && ((guaranteed && i === 0) || rng.chance(BALANCE.dieSpellRareChance));
    const pool = (rare ? rares : commons).filter((id) => !out.includes(id));
    // Pool épuisé (deux sorts communs déjà tirés) : on bascule sur l'autre pool.
    const fallback = (rare ? commons : rares).filter((id) => !out.includes(id));
    const choices = pool.length > 0 ? pool : fallback;
    if (choices.length === 0) break;
    out.push(choices[rng.int(0, choices.length - 1)]);
  }
  return out;
}

/**
 * Tirage de l'affixe *Pipé* — **à l'acquisition** du dé (arbitrage A20).
 *
 * Une face cible X est tirée dans `1..sides`, puis `sides / 2` faces
 * **distinctes**, X exclu, sont remplacées par X : remplacer X par X ne
 * changerait rien, l'exclure est la seule lecture qui donne bien « la moitié
 * des faces remplacées » (DECISIONS §D.18). Sur un d12 : X et 6 faces
 * remplacées, donc 7 chances sur 12 d'afficher X.
 */
export function rollLoadedFaces(sides: number, rng: Rng): LoadedFaces {
  const face = rng.die(sides);
  const pool: number[] = [];
  for (let f = 1; f <= sides; f++) if (f !== face) pool.push(f);
  const replaced: number[] = [];
  const wanted = Math.min(pool.length, Math.floor(sides / 2));
  for (let i = 0; i < wanted; i++) {
    replaced.push(pool.splice(rng.int(0, pool.length - 1), 1)[0]);
  }
  replaced.sort((a, b) => a - b);
  return { face, replaced };
}

/** Assemble un dé Maître, lui donne ses sorts (A3), son *Pipé* (A20) puis son nom. */
export function createMasterDie(
  sides: DieSides,
  rarity: Rarity,
  affixes: Affix[],
  rng: Rng,
  spells?: DieSpellId[],
): MasterDie {
  const base: Omit<MasterDie, 'name'> = {
    id: makeDieId(rng),
    sides,
    rarity,
    affixes,
    spells: spells ?? rollDieSpells(rarity, rng, sides),
  };
  // *Pipé* est tiré une fois pour toutes, au moment où le dé est obtenu.
  // Jamais sur un d100 (A89-4), même si l'appelant impose l'affixe.
  if (affixes.some((a) => a.id === 'loaded') && affixAppliesTo('loaded', sides)) {
    base.loaded = rollLoadedFaces(sides, rng);
  }
  return { ...base, name: generateDieName(base, rng) };
}

/**
 * Dé **Prismatique nommé** (A72-14) — un Prismatique tiré à la *GachaDice v20*
 * est toujours l'un des trois dés nommés, **équiprobables** : « Dé enragé de
 * Guinsoo », « Dé du chef de chantier », « Dé de l'ArchiDicer ». Nom fixe (pas
 * de nom généré), **4 affixes tirés** normalement puis l'**affixe unique** du
 * dé en dernier ; sorts tirés comme pour tout Prismatique (A3).
 *
 * `named` impose le dé (forge de debug, tests) ; sinon il est tiré au `Rng`.
 */
export function createNamedPrismatic(
  sides: DieSides,
  rng: Rng,
  named?: AffixId,
  spells?: DieSpellId[],
): MasterDie {
  const pick =
    NAMED_PRISMATICS.find((n) => n.affix === named) ??
    NAMED_PRISMATICS[rng.int(0, NAMED_PRISMATICS.length - 1)];
  const affixes = rollAffixes(sides, 4, rng);
  affixes.push({ id: pick.affix, value: AFFIX_BY_ID[pick.affix].min });
  const die = createMasterDie(sides, 'prismatic', affixes, rng, spells);
  die.name = pick.name;
  return die;
}

/** Affixe nommé porté par un dé (Guinsoo, Chef de chantier, ArchiDicer), ou `null`. */
export function namedAffixOf(die: MasterDie | null | undefined): AffixId | null {
  if (!die) return null;
  for (const a of die.affixes) if (AFFIX_BY_ID[a.id]?.named) return a.id;
  return null;
}

/**
 * Le « Dé de l'aïeul » : d4 Commun, sans affixe, avec **un sort commun tiré au
 * hasard** — COMMENTS v0.4.1 §1.3 / arbitrage A5. Il est offert à la première
 * partie et à chaque nouvelle run dont l'emplacement 1 est vide.
 */
export function createStarterDie(rng?: Rng): MasterDie {
  // Un d4 : jamais de sort réservé aux d12 et d20 (A98-4).
  const commons = dieSpellsOfPool('common', 4);
  const spells: DieSpellId[] = rng ? [commons[rng.int(0, commons.length - 1)]] : [];
  return {
    id: 'starter',
    sides: 4,
    rarity: 'common',
    affixes: [],
    spells,
    name: "Dé de l'aïeul",
  };
}

/**
 * Score d'un dé = dégâts moyens attendus par lancer (hors sorts).
 * Sert au tri d'inventaire, au bouton « Équiper le meilleur » et à la simulation.
 *
 * Heuristique : le critique est désormais global au lancer, mais *Précis* et
 * *Incisif* restent des contributions propres au dé — on les valorise comme si
 * le dé était seul, ce qui suffit à classer l'inventaire.
 */
export function dieScore(die: MasterDie, stats?: Partial<DerivedStats>): number {
  const baseCrit = stats?.critChance ?? 0.05;
  const baseCritMult = stats?.critMult ?? 1.5;
  const weighted = Math.min(0.95, affixValue(die, 'weighted') + (stats?.masterWeightedChance ?? 0));
  const advantage = affixValue(die, 'advantaged') > 0;
  // Espérance de la face : mélange entre face maximale forcée et tirage
  // uniforme — *Pipé* compris, qui déforme le tirage uniforme.
  const loaded = effectiveLoaded(die);
  let uniform = 0;
  for (let f = 1; f <= die.sides; f++) uniform += applyLoaded(f, loaded) / die.sides;
  let face = weighted * die.sides + (1 - weighted) * uniform;
  if (advantage) {
    // Espérance du max de deux tirages uniformes sur un dé à n faces.
    let e = 0;
    for (let k = 1; k <= die.sides; k++) {
      e += applyLoaded(k, loaded) * ((2 * k - 1) / (die.sides * die.sides));
    }
    const adv = weighted * die.sides + (1 - weighted) * e;
    face = adv;
  }
  const crit = Math.min(1, baseCrit + affixValue(die, 'precise'));
  const critMult = baseCritMult + affixValue(die, 'incisive');
  // Empoisonné : X % des dégâts du lancer par seconde pendant 4 s, ~2 s utiles.
  const poison = 1 + affixValue(die, 'poisoned') * 2;
  // Niveau du dé (A94-5) : la face pèse ×1,5^niveau — « Équiper le meilleur »
  // préfère donc un dé de niveau 10 ; v0.49.0 (A98-6) : ×1000 s'il est Corrupted.
  // *Réplicant* (A98-7) : une copie à chaque lancer, la face compte deux fois.
  const copies = hasAffix(die, 'replicant') ? 2 : 1;
  return face * copies * dieDamageFactor(die, stats) * (1 - crit + crit * critMult) * poison;
}
